import React from "react";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { Form, Formik } from "formik";
import { axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { applyWaiverCode } from "../../utils/validations";
import { useQueryClient } from "@tanstack/react-query";
import useAuthStore from "../../stores/useAuthStore";
import { Button } from "../../components/ui/Button";
import is_money from "../../helpers/is_money";
import { FiGift, FiX, FiAlertTriangle, FiInfo } from "react-icons/fi";

const ApplyWaiverCode = ({ v }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const [codes, setCodes] = React.useState(["", "", "", "", "", ""]);
  const inputRefs = React.useRef([]);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const handleCodeChange = (index, value, setFieldValue) => {
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");

    if (sanitized.length <= 1) {
      const newCodes = [...codes];
      newCodes[index] = sanitized;
      setCodes(newCodes);

      const fullCode = newCodes.join("");
      setFieldValue("waiverCode", fullCode);

      if (sanitized && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e, setFieldValue) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    const newCodes = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
    setCodes(newCodes);
    setFieldValue("waiverCode", pastedData);

    const nextEmptyIndex = newCodes.findIndex(code => !code);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const _applyWaiver = async (values) => {
    try {
      const req = await axiospost(`ms_application_fee/waiver/${v._id}`, {
        waiverCode: values.waiverCode,
        applicantId: user?._id,
        amount: v.amount
      });

      const { data: result } = req;

      if (result && result.type === "success") {
        const { message, type } = result;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["generatedRRR"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
        setshowmodal(false);
      } else {
        throw new Error(result?.message || "Sorry, system could not process your waiver code");
      }
    } catch (error) {
      const message = error?.message || error?.response?.data?.message || "Sorry, system could not process your waiver code";
      swal("warning", message);
    }
  };

  const _show = () => {
    setshowmodal(true);
    setCodes(["", "", "", "", "", ""]);
  };

  const _close = () => {
    setshowmodal(false);
    setCodes(["", "", "", "", "", ""]);
  };

  return (
    <>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={_close}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
          role="document"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiGift className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-lg font-semibold text-white">Apply Waiver Code</h5>
                <p className="text-amber-100 text-sm mt-0.5">
                  Enter your code to bypass payment
                </p>
              </div>
              <button
                onClick={_close}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <FiX className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <FiInfo className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Enter your 6-character waiver code to bypass payment. The code must match the exact fee amount.
                </p>
              </div>
            </div>

            {/* Amount */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-900">Application Fee</span>
                <span className="text-xl font-bold text-neutral-900">{is_money(v?.amount)}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <FiAlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs text-amber-700">Your waiver code must be for this exact amount</span>
              </div>
            </div>

            <Formik
              initialValues={{ waiverCode: "" }}
              validationSchema={applyWaiverCode}
              onSubmit={_applyWaiver}
            >
              {({ isSubmitting, errors, setFieldValue }) => (
                <Form>
                  {/* Code Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-neutral-700 text-center mb-4">
                      Enter Waiver Code
                    </label>
                    <div className="flex justify-center gap-2.5">
                      {codes.map((code, index) => (
                        <input
                          key={index}
                          ref={(el) => (inputRefs.current[index] = el)}
                          type="text"
                          maxLength="1"
                          value={code}
                          onChange={(e) => handleCodeChange(index, e.target.value, setFieldValue)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={(e) => handlePaste(e, setFieldValue)}
                          className="w-12 h-14 text-center text-xl font-bold font-mono uppercase rounded-lg border-2 border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-white text-neutral-900"
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                    {errors?.waiverCode && (
                      <p className="text-sm text-red-600 text-center mt-3">{errors.waiverCode}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      leftIcon={!isSubmitting && <FiGift className="h-4 w-4" />}
                    >
                      {isSubmitting ? "Applying Waiver..." : "Apply Waiver"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={_close}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </motion.div>
      </ReactModal>

      <Button
        variant="outline"
        size="sm"
        disabled={v.verified || v.isverified}
        onClick={_show}
        leftIcon={<FiGift />}
      >
        Apply Waiver Code
      </Button>
    </>
  );
};

export default ApplyWaiverCode;
